// src/Dashboard/pages/user/Lexique/components/LexiqueRow.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaCheck, FaCopy, FaHeart, FaRegHeart, FaVolumeUp } from "react-icons/fa";

import {
  getJapanese,
  getTranslation,
  hasJPChars,
  speakWith,
  strip,
} from "../utils/lexiqueUtils";

function Highlight({ text, q }) {
  const value = String(text ?? "");
  const query = strip(q || "");

  if (!query) return <>{value}</>;

  const index = strip(value).indexOf(query);

  if (index === -1) return <>{value}</>;

  const before = value.slice(0, index);
  const match = value.slice(index, index + q.length);
  const after = value.slice(index + q.length);

  return (
    <>
      {before}
      <mark className="rounded bg-yellow-200 px-0.5 text-slate-900 dark:bg-yellow-500/40 dark:text-white">
        {match}
      </mark>
      {after}
    </>
  );
}

export default function LexiqueRow({ row, q, isFav, toggleFav }) {
  const japonais = getJapanese(row);
  const traduction = getTranslation(row);

  const [toKanaFn, setToKanaFn] = useState(null);
  const [copied, setCopied] = useState(false);
  const triedImport = useRef(false);

  useEffect(() => {
    if (triedImport.current) return;

    triedImport.current = true;

    (async () => {
      try {
        const mod = await import("wanakana");

        if (mod?.toKana) {
          setToKanaFn(() => mod.toKana);
        }
      } catch {
        // La librairie wanakana est optionnelle.
      }
    })();
  }, []);

  const japaneseForSpeech = useMemo(() => {
    if (!japonais) return "";

    if (hasJPChars(japonais)) return japonais;

    const cleaned = japonais
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    return toKanaFn ? toKanaFn(cleaned) : cleaned;
  }, [japonais, toKanaFn]);

  const copyBoth = async () => {
    try {
      await navigator.clipboard.writeText(`${japonais} — ${traduction}`);
      setCopied(true);

      window.setTimeout(() => {
        setCopied(false);
      }, 900);
    } catch {}
  };

  const buttonClass =
    "inline-flex h-8 items-center justify-center gap-1.5 rounded-xl px-2.5 text-[11px] font-bold ring-1 transition hover:-translate-y-0.5";

  return (
    <li className="group px-3 py-2 transition hover:bg-slate-50 dark:hover:bg-slate-900/60 sm:px-4">
      <div className="grid gap-2 lg:grid-cols-[minmax(170px,0.85fr)_minmax(240px,1.35fr)_auto] lg:items-center">
        <div
          title={japonais}
          className="min-w-0 text-sm font-bold leading-5 text-slate-900 dark:text-white lg:truncate"
        >
          <Highlight text={japonais || "—"} q={q} />
        </div>

        <div
          title={traduction}
          className="min-w-0 text-sm leading-5 text-slate-600 dark:text-slate-300 lg:truncate"
        >
          <Highlight text={traduction || "—"} q={q} />
        </div>

        <div className="flex items-center gap-1.5 lg:justify-end">
          <button
            type="button"
            onClick={() => speakWith("ja", japaneseForSpeech, "ja-JP")}
            title="Prononcer en japonais"
            className={`${buttonClass} bg-white text-slate-600 ring-slate-200 hover:bg-slate-100 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-900`}
          >
            <FaVolumeUp />
            JP
          </button>

          <button
            type="button"
            onClick={() => speakWith("fr", traduction, "fr-FR")}
            title="Prononcer en français"
            className={`${buttonClass} bg-white text-slate-600 ring-slate-200 hover:bg-slate-100 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-900`}
          >
            <FaVolumeUp />
            FR
          </button>

          <button
            type="button"
            onClick={copyBoth}
            title="Copier le terme et la traduction"
            className={`${buttonClass} ${
              copied
                ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-100 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-900"
            }`}
          >
            {copied ? <FaCheck /> : <FaCopy />}
            {copied ? "OK" : "Copier"}
          </button>

          <button
            type="button"
            onClick={toggleFav}
            title={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
            className={`${buttonClass} ${
              isFav
                ? "bg-red-50 text-red-700 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20"
                : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-100 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-900"
            }`}
          >
            {isFav ? <FaHeart /> : <FaRegHeart />}
          </button>
        </div>
      </div>
    </li>
  );
}